import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  collection,
  collectionSnapshots,
  documentId,
  Firestore,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import {
  DbStatus,
  Notification,
  NotificationClass,
  UnreadHorseNotification,
  UnreadHorseNotificationClass,
} from '@sleipai/sleip-models';
import { combineLatest, from, map, Observable, of, switchMap } from 'rxjs';

import { chunkIds, mapDocToType } from '../../helpers';
import {
  NEW_RECORDING_RESULT_EVENT_TYPE,
  NOTIFICATIONS_COLLECTION,
  NOTIFICATIONS_IN_QUERY_CHUNK_SIZE,
  UNREAD_HORSE_NOTIFICATIONS_USER_SUBCOLLECTION,
} from '../notifications.constants';

@Injectable()
export class NotificationsService {
  constructor() {}

  private firestore = inject(Firestore);
  private destroyRef = inject(DestroyRef);

  private getRecordingNotificationQuery(recordingId: string, userId: string) {
    return query(
      collection(this.firestore, NOTIFICATIONS_COLLECTION),
      where('eventType', '==', NEW_RECORDING_RESULT_EVENT_TYPE),
      where('dbStatus', '==', DbStatus.ACTIVE),
      where('resourceId', '==', recordingId),
      where('userId', '==', userId),
      limit(1)
    );
  }

  watchActiveRecordingNotification(
    recordingId: string,
    userId: string
  ): Observable<Notification | null> {
    if (!recordingId || !userId) {
      return of(null);
    }

    return collectionSnapshots(
      this.getRecordingNotificationQuery(recordingId, userId)
    ).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([snapshot]) =>
        snapshot ? new NotificationClass(mapDocToType(snapshot)) : null
      )
    );
  }

  watchNotificationsForRecordingsUnread(
    recordingIds: string[],
    userId: string
  ): Observable<Notification[]> {
    if (!recordingIds.length || !userId) {
      return of([]);
    }

    const chunkObservables = chunkIds(
      recordingIds,
      NOTIFICATIONS_IN_QUERY_CHUNK_SIZE
    ).map((chunk) =>
      collectionSnapshots(
        query(
          collection(this.firestore, NOTIFICATIONS_COLLECTION),
          where('eventType', '==', NEW_RECORDING_RESULT_EVENT_TYPE),
          where('dbStatus', '==', DbStatus.ACTIVE),
          where('userId', '==', userId),
          where('viewStatus', '==', false),
          where('resourceId', 'in', chunk)
        )
      ).pipe(
        map((snapshots) =>
          snapshots.map(
            (snapshot) => new NotificationClass(mapDocToType(snapshot))
          )
        )
      )
    );

    return combineLatest(chunkObservables).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((results) => results.flat())
    );
  }

  markRecordingNotificationRead(
    recordingId: string,
    userId: string,
    read = true
  ): Observable<void> {
    return from(
      getDocs(this.getRecordingNotificationQuery(recordingId, userId))
    ).pipe(
      switchMap(({ docs: [notification] }) => {
        if (!notification) {
          return of(undefined);
        }

        return from(updateDoc(notification.ref, { viewStatus: read }));
      })
    );
  }

  watchUnreadNotificationCountsForHorses(
    horseIds: string[],
    userId: string
  ): Observable<UnreadHorseNotification[]> {
    if (!horseIds.length || !userId) {
      return of([]);
    }

    const unreadHorsesCollection = collection(
      this.firestore,
      `users/${userId}/${UNREAD_HORSE_NOTIFICATIONS_USER_SUBCOLLECTION}`
    );

    const chunkObservables = chunkIds(
      horseIds,
      NOTIFICATIONS_IN_QUERY_CHUNK_SIZE
    ).map((chunk) =>
      collectionSnapshots(
        query(unreadHorsesCollection, where(documentId(), 'in', chunk))
      ).pipe(
        map((snapshots) =>
          snapshots.map(
            (snapshot) =>
              new UnreadHorseNotificationClass(mapDocToType(snapshot))
          )
        )
      )
    );

    return combineLatest(chunkObservables).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((results) => results.flat())
    );
  }
}
